import { ChevronRight, Users } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Progress } from '@/components/ui/progress';
import { UniversalCrudActions } from '@/components/UniversalCrudActions';
import { cn, formatCurrency } from '@/lib/utils';

const formatShortDate = (value) => {
  if (!value) return '—';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return parsed.toLocaleDateString('pt-MZ');
};

export const PayrollEmployeeDirectorySection = ({
  sortedEmployees,
  employeeSortField,
  employeeSortDirection,
  employeeSortOptions,
  employeeDirectory,
  selectedEmployeeIds,
  allVisibleEmployeesSelected,
  canManagePayroll,
  openPayrollEmployeeDialog,
  toggleAllVisibleEmployees,
  handleBulkProcessEmployees,
  toggleEmployeeSelection,
  openDossierDialog,
}) => (
  <Card className="overflow-hidden rounded-2xl border-slate-200 shadow-sm" data-testid="payroll-employees-list-card">
    <CardHeader className="border-b border-slate-200 bg-white/90 px-4 py-3 md:px-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <Users className="h-5 w-5" />
            Dossier digital ({sortedEmployees.length})
          </CardTitle>
        </div>
        <div className="flex flex-wrap gap-2 text-xs">
          <Badge variant="outline" className="bg-white">Ordenação: {employeeSortOptions.find((option) => option.value === employeeSortField)?.label || employeeSortField}</Badge>
          <Badge variant="outline" className="bg-white">Direcção: {employeeSortDirection === 'asc' ? 'Ascendente' : 'Descendente'}</Badge>
          <Badge variant="outline" className="bg-white">Incompletos: {employeeDirectory.incomplete}</Badge>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="payroll-employees-bulk-toolbar">
        {selectedEmployeeIds.length > 0 && (
          <Badge variant="outline" className="bg-white" data-testid="payroll-employees-selection-badge">{selectedEmployeeIds.length} seleccionado(s)</Badge>
        )}
        {canManagePayroll && (
          <Button variant="outline" size="sm" onClick={openPayrollEmployeeDialog} data-testid="payroll-create-employee-button">
            Novo colaborador RH
          </Button>
        )}
        <Button variant="outline" size="sm" onClick={() => toggleAllVisibleEmployees(!allVisibleEmployeesSelected)} data-testid="payroll-employees-select-all-visible-button">
          {allVisibleEmployeesSelected ? 'Limpar visíveis' : 'Seleccionar visíveis'}
        </Button>
        <Button variant="outline" size="sm" disabled={!selectedEmployeeIds.length} onClick={handleBulkProcessEmployees} data-testid="payroll-employees-bulk-process-button">
          Processar seleccionados
        </Button>
      </div>
    </CardHeader>
    <CardContent className="p-0">
      {sortedEmployees.length === 0 ? (
        <div className="py-10 text-center text-sm text-muted-foreground" data-testid="payroll-employees-empty-state">
          Nenhum colaborador corresponde aos filtros activos.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="sticky top-0 z-10 border-b bg-slate-50/95 backdrop-blur">
              <tr>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                  <Checkbox checked={allVisibleEmployeesSelected} onCheckedChange={(checked) => toggleAllVisibleEmployees(Boolean(checked))} data-testid="payroll-employees-select-all-checkbox" />
                </th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Funcionário</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Cargo / Perfil</th>
                <th className="px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Salário base</th>
                <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Dossier</th>
                <th className="px-3 py-2 text-center text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">Acções</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedEmployees.map((employee) => (
                <tr key={employee.id} className={cn('hover:bg-slate-50/50', selectedEmployeeIds.includes(employee.id) && 'bg-slate-100/80')} data-testid={`payroll-employee-row-${employee.id}`}>
                  <td className="px-3 py-2 align-top">
                    <Checkbox checked={selectedEmployeeIds.includes(employee.id)} onCheckedChange={(checked) => toggleEmployeeSelection(employee.id, Boolean(checked))} disabled={employee.payroll_eligible === false} data-testid={`payroll-employee-select-checkbox-${employee.id}`} />
                  </td>
                  <td className="px-3 py-2 align-top">
                    <div>
                      <p className="text-sm font-medium text-slate-900">{employee.full_name || employee.name}</p>
                      <p className="text-[11px] text-slate-500">Colab. {employee.employee_number || '—'}</p>
                    </div>
                  </td>
                  <td className="px-3 py-2 align-top text-sm text-slate-700">{employee.profile_role_label || employee.role || '—'}</td>
                  <td className="px-3 py-2 text-right align-top text-sm font-medium text-slate-900">{formatCurrency(employee.base_salary || 0)}</td>
                  <td className="px-3 py-2 align-top">
                    <div className="space-y-1">
                      <Badge className={cn('text-[11px]', employee.dossier_complete ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700')}>
                        {employee.dossier_complete ? '100% Fechado' : 'Em falta'}
                      </Badge>
                      <Badge variant="outline" className={cn('text-[11px]', employee.employment_state === 'offboarded' ? 'border-rose-200 bg-rose-50 text-rose-700' : 'border-emerald-200 bg-emerald-50 text-emerald-700')} data-testid={`payroll-employee-status-${employee.id}`}>
                        {employee.employment_state === 'offboarded' ? 'Saído / Suspenso' : 'Activo'}
                      </Badge>
                      <div className="flex items-center gap-2">
                        <Progress value={employee.completion_percentage || 0} className="h-1.5 w-24" />
                        <span className="text-[11px] text-slate-500">{employee.completion_percentage || 0}%</span>
                      </div>
                      {employee.contract_end_date && (
                        <p className="text-[11px] text-slate-500" data-testid={`payroll-employee-exit-date-${employee.id}`}>
                          Saída: {formatShortDate(employee.contract_end_date)}
                        </p>
                      )}
                    </div>
                  </td>
                  <td className="px-2 py-2 align-top">
                    <div className="flex justify-center">
                      <UniversalCrudActions
                        entityLabel="employee-dossier"
                        entityId={employee.id}
                        canView={false}
                        canEdit={canManagePayroll}
                        canDelete={false}
                        canRecover={false}
                        align="center"
                        onEdit={() => openDossierDialog(employee)}
                        extraActions={(
                          <div className="flex items-center justify-center rounded-xl border border-slate-200 bg-white/90 p-1">
                            <Button variant="ghost" size="sm" className="h-7 rounded-lg px-2 text-slate-500" onClick={() => openDossierDialog(employee)} data-testid={`payroll-employee-open-${employee.id}`}>
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        )}
                      />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </CardContent>
  </Card>
);